// WS-36: northern-lights settings: on/off, direction (compass bearing of the band centre), height (horizon ..
// overhead) and intensity. Resort-agnostic; main.js hands over the engine side when resort.aurora.controls is set.
// Lives under the map controls' aurora button (#aurora-btn), built like the trees popover (WS-18): the button opens
// the popover instead of toggling, and on/off is the switch inside. The button's gold "on" state still shows whether
// the aurora is on. Layout: a dial (a sky chart: centre = overhead, rim = horizon). The user picked the dial over the
// sliders option on 2026-10-12.
// WS-49: the popover is the control `aurora.panel`. The interface (js/chrome/column.js) says which button it hangs on
// while the preset shows it (auroraHost); without it the button is the plain on/off toggle. Direction, height,
// intensity and "Play a night's display" are set through the runtime (aurora.bearing, .elevation, .intensity, .auto, as a visitor); on/off and "Show at
// night" call the map's own functions, which report to the runtime. Every change is written back to the URL
// (&aurora, &auroraBearing, &auroraElev, &auroraIntensity, &auroraAuto).
// WS-47:
//  - with an activity look (aurora.levels: resort.aurora.look) the slider is "Strength" on a named scale (Calm ..
//    Storm): it changes the kind of display (form, motion, colour), so it reads as activity, not as a percentage.
//  - "Play a night's display" (setting aurora.auto, package aurora.cycle; aurora.cycleState / setCycle): off by
//    default. While it plays, the handle and the thumb stay where the guest put them (centre and peak: a control
//    never moves by itself); the live level and phase are said in words.
//  - "Explain what's happening" (aurora.cycle.guide): a caption at the top of the map names the part of the display
//    that is going on, in plain words; its switch is under the play switch while the display plays. The caption
//    does not need the popover: a preset that hides `aurora.panel` and plays the display still gets it.
//  - the dial turns and raises separately (user 2026-10-13: "badly optimized for changing direction"). One pointer
//    position used to set both, so a turn meant dragging along an exact circle. Now: a drag is relative and locks to
//    its dominant axis (sweep around the centre = turn, towards or away from it = raise); the rim is a pure turn
//    zone; the compass letters and diagonal dots are tap targets; the wheel turns; taps ease to their target.
//  - the handle can be grabbed (user 2026-10-15: "grabbing the dot in the middle is laggy", "clicking in the middle
//    randomly rotates"): a press on the dot drags it where the pointer goes; near the centre it snaps overhead and
//    keeps its direction, so it never spins; a tap at the centre = overhead, same direction. While the dial is used
//    the map draws at full rate (wake), not the idle 30 fps.
import { html } from './dom.js';
import { setAddress } from './address.js';
import { i18n } from './i18n.js';
import { PHASES } from './auroraCycle.js';
import { sunPosition, localToUTC, skyAt, mapYear } from './sun.js';
import { fit } from './chrome/popup.js';

const POINTS = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw']; // interface keys dir.<point>, aurora.turn.<point>
const RIM = 84; // dial horizon radius (svg units; the svg is 208 units wide)
const RIM_ZONE = 0.8, LOCK = 5, CENTRE = 12, GRAB = 9, SNAP = 6; // rim turn zone from 80 % of the radius; axis lock after 5 units; no direction within 12 units of the centre; the handle (r 7) grabs within 9 units (a finger: 16); overhead within 6
const dAng = (a, b) => ((((a - b) % 360) + 540) % 360) - 180; // signed shortest turn from b to a

let deps = null, host = null, live = null, self = false, guide = null; // the engine side, the button to hang on, the popover, "my own change", the phase caption
const mount = () => { live?.destroy(); live = deps && host ? build(deps, host) : null; };

/** main.js: the engine side of the panel (the aurora effect, the camera, the sky view). */
export function createAuroraPanel(opts) {
  deps = opts;
  opts.skyView?.listen(() => live?.sync());
  const cy = opts.aurora.cycleState();
  if (cy.available && cy.guide !== false && !guide) guide = createGuide(opts.aurora, cy.guide);
  mount();
  return { sync: () => live?.sync(), open: (on, focus) => live?.open(on, focus), get el() { return live?.el || null; } };
}
/** The interface: the aurora button the popover hangs on and its context, or null while `aurora.panel` is hidden. */
export function auroraHost(btn, c) { host = btn ? { btn, c } : null; mount(); }
/** The interface: aurora values changed somewhere else (a tour, the editor). */
export function auroraSync() { if (!self) live?.sync(); }
/** VIS-128: the sky is dark enough for the lights at the map's date and hour (the plain button says so in daylight too). */
export const isDark = (s) => skyAt(sunPosition(localToUTC(mapYear(s), s.month, s.day, s.hour)).alt).night > 0.5; // WS-77: the map's year
const styles = () => {
  if (document.querySelector('link[data-ws36]')) return;
  const l = document.createElement('link'); l.rel = 'stylesheet'; l.href = 'css/aurora-controls.css'; l.dataset.ws36 = '1';
  document.head.appendChild(l);
};
// the name and the sentence of a part: the package's own copy (aurora.cycle.names / lines), else the interface's
const partName = (cy, i) => i18n.named(cy.names?.[i], i18n.T(`aurora.part.${PHASES[i]}`));
const partLine = (cy, i) => i18n.named(cy.lines?.[i], i18n.T(`aurora.part.${PHASES[i]}.line`));

// ---- WS-47 phase guide (begin): #aurora-guide, a caption at the top centre (dots: done / now / to come, the part's
// name, one sentence). It shows while the display plays at night with the guide on, never takes the pointer, and
// changes with a short cross-fade when the part changes (cycleState().noteIndex: the breakup's caption stays at least
// noteHold s). It keeps clear of the top chrome: in the free stretch of the top row when it fits there, else below it
// (below the sky view's bar too), left of an open place card; where a card would cover it (phones) it hides.
function createGuide(aurora, cfg) {
  styles();
  const cap = document.createElement('div'), still = matchMedia('(prefers-reduced-motion: reduce)');
  cap.id = 'aurora-guide'; cap.className = 'aur-guide'; cap.setAttribute('role', 'status'); cap.setAttribute('aria-atomic', 'true'); // announced politely, on a change of part
  cap.innerHTML = html`<span class="dots" aria-hidden="true"><i></i><i></i><i></i><i></i></span><b></b><span class="line"></span>`;
  document.body.appendChild(cap);
  const rects = (sel) => [...document.querySelectorAll(sel)].filter((e) => getComputedStyle(e).opacity !== '0').map((e) => e.getBoundingClientRect()).filter((r) => r.width);
  function place() { // false = no room
    const w = cap.offsetWidth, h = cap.offsetHeight, M = 10, c = innerWidth / 2, card = rects('#card.open')[0], side = card && card.top < 200;
    const lo = M, hi = (side ? card.left : innerWidth) - M - w, obs = rects('.brand, .seasons, .top-actions, .skyview-bar');
    let x = Math.max(lo, Math.min(hi, c - w / 2)), y = innerWidth <= 760 ? 14 : 24;
    for (let k = 0; k < 4; k++) {
      const row = obs.filter((o) => o.top < y + h + M && o.bottom > y - M);
      if (!row.some((o) => o.left < x + w + M && o.right > x - M)) break;
      const l = Math.max(lo, ...row.filter((o) => o.right <= c).map((o) => o.right + M)), r = Math.min(hi + w, ...row.filter((o) => o.left >= c).map((o) => o.left - M));
      if (!row.some((o) => o.left < c && o.right > c) && r - l >= w) { x = Math.max(l, Math.min(r - w, x)); y = row.reduce((a, o) => a + o.top + o.bottom, 0) / row.length / 2 - h / 2; break; } // in the row, on its centre line
      y = Math.max(...row.map((o) => o.bottom)) + M;
    }
    cap.style.left = `${Math.round(x)}px`; cap.style.top = `${Math.round(y)}px`;
    return hi >= lo && !(card && !side && card.top < y + h + M);
  }
  let shown = '';
  const g = {
    on: cfg === true || cfg.default !== false, // the guest's choice (the switch in the popover)
    step() {
      if (!shown && !(g.on && aurora.uniforms.strength.value > 0.5)) return; // WS-77: nothing to show and none shown (by day, the aurora off): the 4 Hz check does nothing
      const cy = aurora.cycleState(), show = g.on && cy.on && aurora.uniforms.strength.value > 0.5, i = cy.noteIndex, key = `${i}|${i18n.lang}`;
      if (show && key !== shown) {
        const set = () => {
          cap.querySelector('b').textContent = partName(cy, i); cap.querySelector('.line').textContent = partLine(cy, i);
          cap.querySelectorAll('i').forEach((d, k) => { d.className = k < i ? 'done' : k === i ? 'now' : ''; });
          cap.classList.remove('swap');
        };
        if (shown && cap.classList.contains('on') && !still.matches) { cap.classList.add('swap'); setTimeout(set, 190); } else set();
        shown = key;
      }
      if (!show) shown = '';
      cap.classList.toggle('on', show && place());
    },
  };
  setInterval(g.step, 250);
  return g;
}
// ---- WS-47 phase guide (end)

function build({ aurora, getState, camera, controls, setOn, setTime, lookAt, skyView = null }, { btn, c }) {
  const T = i18n.T, num = i18n.num, ac = new AbortController();
  const compass = (b) => T(`dir.${POINTS[Math.round(b / 45) % 8]}`);
  styles();

  // ---------------------------------------------------------------- the aurora button opens the popover
  btn.title = T('aurora.title');
  btn.setAttribute('aria-haspopup', 'dialog'); btn.setAttribute('aria-expanded', 'false'); btn.setAttribute('aria-controls', 'aurora-pop');

  const pop = document.createElement('div');
  pop.id = 'aurora-pop'; pop.className = 'aur-pop'; pop.setAttribute('role', 'dialog'); pop.setAttribute('aria-label', T('aurora.title')); pop.hidden = true;
  const ring = (el) => RIM * (1 - el / 90);
  // WS-47: named activity scale; the slider runs from the calm level to the storm level (halfway between two levels reads as the upper one)
  // the engine's levels are named by the interface (aurora.act.<id>); a package's own names are package labels
  const LV = aurora.levels, names = LV ? LV.names.map((n, k) => (LV.own ? i18n.named(n, T(`aurora.act.${LV.ids[k]}`)) : T(`aurora.act.${LV.ids[k]}`)) || n) : [];
  const lvName = (v) => names[LV.values.reduce((b, x, i) => (Math.abs(x - v) <= Math.abs(LV.values[b] - v) + 1e-9 ? i : b), 0)];
  const strengthRow = LV
    ? html`<label class="tp-row"><span>${T('aurora.strength')} <b id="aur-ival"></b></span>
      <input id="aur-int" type="range" min="${Math.round(LV.values[0] * 100)}" max="${Math.round(LV.max * 100)}" step="5" aria-label="${T('aurora.strength.label', { from: i18n.lower(names[0]), to: i18n.lower(names[names.length - 1]) })}">
      <span class="aur-scale" aria-hidden="true"><i>${names[0]}</i><i>${names[names.length - 1]}</i></span></label>`
    : html`<label class="tp-row"><span>${T('aurora.intensity')} <b id="aur-ival"></b></span>
      <input id="aur-int" type="range" min="10" max="150" step="5" aria-label="${T('aurora.intensity.label')}"></label>`;
  const hasCycle = aurora.cycleState().available, G = hasCycle ? guide : null; // G: the phase caption (null: none)
  // the caption switch is a viewer preference of today's page: remembered there, never read under a preset
  if (G && c.legacy && !G.read) { G.read = true; try { const v = localStorage.getItem('mms-aurora-guide'); if (v) G.on = v === '1'; } catch { /* errors:ignore: storage blocked */ } }
  // compass tap targets around the rim (percent of the dial box; letters on the four points, dots on the diagonals)
  const dirs = POINTS.map((k, i) => html`<button class="aur-dir${i % 2 ? ' dot' : ''}${i === 0 ? ' n' : ''}" data-b="${i * 45}" aria-label="${T(`aurora.turn.${k}`)}">${i % 2 ? '' : T(`dir.${k}`)}</button>`);
  pop.innerHTML = html`
    <div class="aur-head"><span class="eyebrow">${T('aurora.title')}</span>
      <button class="tp-switch" id="aur-on" role="switch" aria-label="${T('aurora.switch')}"><i></i></button></div>
    <div class="aur-dial">
      <svg viewBox="-104 -104 208 208" aria-hidden="true">
        <defs><radialGradient id="aur-sky"><stop offset="0" stop-color="#16243f"/><stop offset="1" stop-color="#0a1120"/></radialGradient>
          <linearGradient id="aur-band" x1="0" x2="1"><stop offset="0" stop-color="#7dffb4" stop-opacity="0.15"/><stop offset="0.5" stop-color="#7dffb4"/><stop offset="1" stop-color="#7dffb4" stop-opacity="0.15"/></linearGradient></defs>
        <circle r="${RIM}" class="aur-skydisc"/>
        <path class="aur-wedge" d=""/>
        <circle r="${ring(30)}" class="aur-ring"/><circle r="${ring(60)}" class="aur-ring"/>
        <path class="aur-cross" d="M0 ${-RIM}V${RIM}M${-RIM} 0H${RIM}"/>
        <text class="aur-deg" x="3" y="${-ring(30) + 9}">30°</text><text class="aur-deg" x="3" y="${-ring(60) + 9}">60°</text>
        <circle r="${RIM * (1 + RIM_ZONE) / 2}" class="aur-turnzone" stroke-width="${RIM * (1 - RIM_ZONE)}"/>
        <path class="aur-glow" d=""/><path class="aur-path" d=""/>
        <circle class="aur-handle" r="7"/>
      </svg>
      <div class="aur-hit" id="aur-dial" role="slider" tabindex="0" aria-label="${T('aurora.dial')}" aria-valuemin="0" aria-valuemax="360"></div>
      ${dirs}
    </div>
    <p class="aur-read" id="aur-read"></p>
    ${strengthRow}
    ${hasCycle ? html`<div class="tp-row aur-cycrow"><span id="aur-cyc-lab">${T('aurora.cycle')}</span>
      <button class="tp-switch" id="aur-cyc" role="switch" aria-labelledby="aur-cyc-lab" aria-describedby="aur-cyc-note"><i></i></button>
      <small class="tp-note" id="aur-cyc-note" aria-live="polite"></small></div>` : ''}
    ${G ? html`<div class="tp-row aur-cycrow" id="aur-guide-row" hidden><span id="aur-guide-lab">${T('aurora.guide')}</span>
      <button class="tp-switch" id="aur-guide-sw" role="switch" aria-labelledby="aur-guide-lab"><i></i></button></div>` : ''}
    <div class="aur-foot"><small class="tp-note" id="aur-note" hidden></small>
      <div class="aur-actions">${skyView ? html`<button class="aur-look ghost" id="aur-lookup" hidden></button>` : ''}<button class="aur-look" id="aur-look"></button></div></div>`;
  document.body.appendChild(pop);
  const $ = (s) => pop.querySelector(s);

  // ---------------------------------------------------------------- state -> UI
  const isNight = () => isDark(getState());
  const elevText = (e) => (e >= 89 ? T('aurora.overhead') : T('aurora.up', { n: num(Math.round(e)) }));
  const percent = (v) => T('percent', { n: num(Math.round(v * 100)) });
  // "Show at night" moves the hour, so it is offered only where the visitor may change the time
  const canTime = () => c.has('time.hour') || c.has('time.play');
  const base = () => aurora.get(); // what the guest set (a night's display plays around it and never changes it)
  let phaseShown = null;
  function syncCycle(p, cy) {
    const sw = $('#aur-cyc');
    sw.classList.toggle('on', cy.on); sw.setAttribute('aria-checked', String(cy.on));
    // the time scale is said plainly: the package's words (aurora.cycle.says), else in numbers
    const scale = () => i18n.named(cy.says, T('aurora.cycle.scale', { hours: num(cy.hours), minutes: num(cy.length / 60, cy.length % 60 ? 1 : 0) }));
    const text = cy.on ? T('aurora.cycle.now', { part: i18n.lower(partName(cy, cy.noteIndex)) }) : T('aurora.cycle.note', { scale: scale() });
    if (text !== phaseShown) { phaseShown = text; $('#aur-cyc-note').textContent = text; } // a live region: only on change
    if (cy.on) $('#aur-ival').textContent = T('aurora.cycle.level', { now: lvName(cy.activity), peak: lvName(p.intensity) });
    if (G) { $('#aur-guide-row').hidden = !cy.on; $('#aur-guide-sw').classList.toggle('on', G.on); $('#aur-guide-sw').setAttribute('aria-checked', String(G.on)); }
  }
  function sync() {
    const p = aurora.get(), on = getState().auroraOn;
    // the slider shows the visitor's own value (the effect's is that value times the preset's aurora.level)
    const set = c.v('aurora.intensity'), amount = !Number.isFinite(set) ? p.intensity : set;
    $('#aur-on').classList.toggle('on', on); $('#aur-on').setAttribute('aria-checked', String(on));
    btn.classList.toggle('on', on); // the button's gold state = the aurora is on (as without the popover)
    pop.classList.toggle('is-off', !on);
    $('#aur-int').value = Math.round(amount * 100); $('#aur-ival').textContent = LV ? lvName(amount) : percent(amount);
    if (LV) $('#aur-int').setAttribute('aria-valuetext', lvName(amount));
    if (hasCycle) syncCycle(p, aurora.cycleState());
    const r = ring(p.elevation), a = (p.bearing * Math.PI) / 180;
    const h = $('.aur-handle'); h.setAttribute('cx', (r * Math.sin(a)).toFixed(1)); h.setAttribute('cy', (-r * Math.cos(a)).toFixed(1));
    const pts = aurora.bandPath(64).map(({ az, el }) => { const rr = ring(el), aa = (az * Math.PI) / 180; return `${(rr * Math.sin(aa)).toFixed(1)} ${(-rr * Math.cos(aa)).toFixed(1)}`; });
    const d = pts.length > 1 ? `M${pts.join('L')}` : '';
    $('.aur-path').setAttribute('d', d); $('.aur-glow').setAttribute('d', d);
    const said = { dir: compass(p.bearing), bearing: num(Math.round(p.bearing)), height: elevText(p.elevation) };
    $('#aur-read').textContent = T('aurora.read', said);
    const dial = $('#aur-dial'); dial.setAttribute('aria-valuenow', String(Math.round(p.bearing))); dial.setAttribute('aria-valuetext', T('aurora.value', said));
    const night = isNight();
    const note = !on || night ? '' : T('aurora.needsDark');
    $('#aur-note').textContent = note; $('#aur-note').hidden = !note;
    $('#aur-look').textContent = T(night ? 'aurora.face' : 'aurora.night');
    $('#aur-look').hidden = !on || (!night && !canTime());
    if (skyView) { // a toggle: the same button leads up and back
      const b = $('#aur-lookup'), up = skyView.active;
      b.hidden = !up && (!on || !night || !skyView.usable());
      b.textContent = T(up ? 'aurora.back' : 'aurora.lookUp');
      b.setAttribute('aria-label', T(up ? 'aurora.back' : 'aurora.lookUp.label')); b.title = up ? '' : T('aurora.lookUp.label');
      b.setAttribute('aria-pressed', String(up));
    }
  }

  // ---------------------------------------------------------------- UI -> state (+ URL)
  let urlT = 0;
  function writeUrl() {
    clearTimeout(urlT);
    urlT = setTimeout(() => {
      const p = base();
      setAddress({ aurora: getState().auroraOn ? 'on' : 'off', auroraBearing: String(Math.round(p.bearing)), auroraElev: String(Math.round(p.elevation)), auroraIntensity: String(+p.intensity.toFixed(2)),
        ...(hasCycle && { auroraAuto: aurora.cycleState().on ? '1' : null }) }); // WS-47: null removes the parameter
    }, 250);
  }
  // a visitor's change of the bands, through the runtime; the popover's own sync follows at once
  const change = ({ bearing, elevation, intensity }) => {
    self = true;
    // direction and height always go together: the map re-reads both from the runtime, so putting one alone brought
    // back an older value of the other (a turn that moved the height, a raise that turned)
    const now = base();
    bearing ??= now.bearing; elevation ??= now.elevation;
    try {
      c.put({
        ...(bearing != null && { 'aurora.bearing': ((bearing % 360) + 360) % 360 }),
        ...(elevation != null && { 'aurora.elevation': Math.min(90, Math.max(2, elevation)) }),
        ...(intensity != null && { 'aurora.intensity': intensity }),
      });
    } finally { self = false; }
    sync(); writeUrl();
  };
  $('#aur-on').onclick = () => { setOn(!getState().auroraOn); sync(); writeUrl(); };
  $('#aur-int').addEventListener('input', (e) => change({ intensity: Number(e.target.value) / 100 }));
  // through the runtime (aurora.auto, as a visitor), so a preset, a tour and capture() all see it; main.js applies it
  if (hasCycle) $('#aur-cyc').onclick = () => {
    const want = !aurora.cycleState().on;
    let res; self = true; try { res = c.put({ 'aurora.auto': want }); } finally { self = false; }
    // the runtime applies a change of its own value only: when the display was started some other way (a script, a
    // tour) its value may already say `want`, and the switch must still act (a control is never a dead press)
    if (!res?.rejected?.length && aurora.cycleState().on !== want) aurora.setCycle(want);
    sync(); writeUrl();
  };
  if (G) $('#aur-guide-sw').onclick = () => {
    G.on = !G.on;
    if (c.legacy) try { localStorage.setItem('mms-aurora-guide', G.on ? '1' : '0'); } catch { /* errors:ignore: storage blocked */ }
    sync(); G.step();
  };
  $('#aur-look').onclick = () => {
    if (isNight()) { // WS-47: always something visible. Looking up: the view eases back onto the band. On the map: a
      // band too high for the map camera is looked up at; a low one gets the map's night framing.
      if (skyView?.active) skyView.aim();
      else if (skyView && skyView.usable() && aurora.get().elevation > skyView.above) lookUp('band');
      else lookAt();
      return;
    }
    setTime({ hour: 22 }); // the sun glides (1.6 s); then frame the aurora
    setTimeout(() => { if (!ac.signal.aborted) { sync(); lookAt(); } }, 1800);
  };
  // entering keeps the popover open (the dial is how the guest moves the lights while watching them), except on
  // phones, where it would cover the sky
  const lookUp = (to = 'up') => { if (innerWidth <= 760) open(false); skyView.enter({ to }); };
  if (skyView) $('#aur-lookup').onclick = () => (skyView.active ? skyView.exit() : lookUp());

  // ---- taps ease to their target (a short turn the shortest way round); drags follow the pointer directly
  let easeRaf = 0;
  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const cancelEase = () => { cancelAnimationFrame(easeRaf); easeRaf = 0; };
  function easeTo(target, ms = 360) {
    cancelEase();
    const from = base(), db = target.bearing == null ? 0 : dAng(target.bearing, from.bearing), de = target.elevation == null ? 0 : target.elevation - from.elevation;
    if (still.matches || ms <= 0) { change({ bearing: from.bearing + db, elevation: from.elevation + de }); return; }
    const t0 = performance.now();
    const step = (now) => {
      const k = Math.min(1, (now - t0) / ms), s = k * k * (3 - 2 * k);
      change({ bearing: from.bearing + db * s, elevation: from.elevation + de * s }); deps?.wake?.();
      easeRaf = k < 1 ? requestAnimationFrame(step) : 0;
    };
    easeRaf = requestAnimationFrame(step);
  }
  for (const b of pop.querySelectorAll('.aur-dir')) {
    const a = (Number(b.dataset.b) * Math.PI) / 180, rr = ((RIM + (b.classList.contains('dot') ? 6 : 10)) / 208) * 100; // percent of the dial box
    b.style.left = `${(50 + rr * Math.sin(a)).toFixed(2)}%`; b.style.top = `${(50 - rr * Math.cos(a)).toFixed(2)}%`; // set from script (no inline style attributes: CSP)
    b.onclick = () => easeTo({ bearing: Number(b.dataset.b) });
  }

  // ---- the dial: a drag is relative to where it started and locks to one axis
  const hit = $('#aur-dial'), svg = $('svg');
  const polar = (e) => {
    const r = svg.getBoundingClientRect(), k = 208 / r.width, x = (e.clientX - r.left) * k - 104, y = (e.clientY - r.top) * k - 104;
    return { x, y, r: Math.hypot(x, y), a: (Math.atan2(x, -y) * 180) / Math.PI };
  };
  // a pointer position as a band position: overhead (direction kept) at the centre, so the dial never spins there
  const place = (q) => (q.r <= SNAP ? { elevation: 90 } : { elevation: 90 * (1 - Math.min(RIM, q.r) / RIM), ...(q.r > CENTRE * 0.8 ? { bearing: q.a } : {}) });
  const wake = () => deps?.wake?.(); // the map draws at full rate while the dial is used
  let g = null;
  hit.addEventListener('pointerdown', (e) => {
    cancelEase(); wake();
    const q = polar(e), p = base(), hr = ring(p.elevation), ha = (p.bearing * Math.PI) / 180;
    const hx = hr * Math.sin(ha), hy = -hr * Math.cos(ha), onDot = Math.hypot(q.x - hx, q.y - hy) <= (e.pointerType === 'touch' ? 16 : GRAB);
    // on the dot: it follows the pointer (keeping where it was taken); the rim: turn; elsewhere the drag decides
    g = { start: q, last: q, bearing: p.bearing, elevation: p.elevation, turned: 0, moved: false, dx: hx - q.x, dy: hy - q.y,
      mode: onDot ? 'grab' : q.r >= RIM * RIM_ZONE ? 'turn' : null };
    hit.setPointerCapture(e.pointerId); pop.classList.toggle('aur-turning', g.mode === 'turn'); e.preventDefault();
  });
  hit.addEventListener('pointermove', (e) => {
    if (!g) return;
    const q = polar(e), dx = q.x - g.start.x, dy = q.y - g.start.y;
    if (!g.moved && Math.hypot(dx, dy) < (g.mode === 'grab' ? 2 : LOCK)) return; // still a tap
    g.moved = true; wake();
    if (g.mode === 'grab') { // the dot goes where the pointer goes (no lock, no axis)
      const x = q.x + g.dx, y = q.y + g.dy;
      change(place({ x, y, r: Math.hypot(x, y), a: (Math.atan2(x, -y) * 180) / Math.PI }));
      g.last = q; return;
    }
    // radial unit vector of the gesture: from the centre to where it started, or along the band's bearing at the centre
    const b = (g.bearing * Math.PI) / 180, far = g.start.r >= CENTRE;
    const rx = far ? g.start.x / g.start.r : Math.sin(b), ry = far ? g.start.y / g.start.r : -Math.cos(b);
    if (!g.mode) {
      if (!far && g.elevation >= 85) g.mode = 'pull'; // overhead has no direction yet: pull the band down towards one
      else if (!far) g.mode = 'raise';                // at the centre a sweep has no meaning, so it never turns (no flips)
      else g.mode = Math.abs(-dx * ry + dy * rx) > Math.abs(dx * rx + dy * ry) ? 'turn' : 'raise';
      pop.classList.toggle('aur-turning', g.mode === 'turn');
    }
    if (g.mode === 'turn') {
      if (q.r > CENTRE * 0.8 && g.last.r > CENTRE * 0.8) g.turned += dAng(q.a, g.last.a); // the swept angle, accumulated
      change({ bearing: g.bearing + g.turned });
    } else if (g.mode === 'raise') change({ elevation: g.elevation - ((dx * rx + dy * ry) * 90) / RIM });
    else if (q.r > CENTRE * 0.8) change(place(q));
    g.last = q;
  });
  const end = (e) => {
    if (!g) return;
    // a tap: go there (on the dot: stay; at the centre: overhead, direction kept)
    if (!g.moved && e.type === 'pointerup' && g.mode !== 'grab') easeTo(g.mode === 'turn' ? { bearing: g.start.a } : g.start.r < CENTRE ? { elevation: 90 } : place(g.start));
    g = null; pop.classList.remove('aur-turning');
  };
  hit.addEventListener('pointerup', end);
  hit.addEventListener('pointercancel', end);
  hit.addEventListener('wheel', (e) => { e.preventDefault(); cancelEase(); wake(); change({ bearing: base().bearing + Math.sign(e.deltaY) * 5 }); }, { passive: false });
  hit.addEventListener('keydown', (e) => {
    const p = base(), st = e.shiftKey ? 1 : 5;
    const k = { ArrowLeft: { bearing: p.bearing - st }, ArrowRight: { bearing: p.bearing + st }, ArrowUp: { elevation: p.elevation + st }, ArrowDown: { elevation: p.elevation - st } }[e.key];
    if (!k) return;
    e.preventDefault(); e.stopPropagation(); // the map's own arrow keys pan
    cancelEase(); wake(); change(k);
  });

  // ---------------------------------------------------------------- open / close; the dial shows the camera's view
  let raf = 0, lastSync = 0;
  function wedge(now = 0) {
    if (camera && controls) {
      const dx = controls.target.x - camera.position.x, dz = controls.target.z - camera.position.z;
      const b = Math.atan2(dx, -dz), hf = Math.atan(Math.tan(((camera.fov / 2) * Math.PI) / 180) * camera.aspect);
      const p1 = [RIM * Math.sin(b - hf), -RIM * Math.cos(b - hf)], p2 = [RIM * Math.sin(b + hf), -RIM * Math.cos(b + hf)];
      $('.aur-wedge').setAttribute('d', `M0 0L${p1[0].toFixed(1)} ${p1[1].toFixed(1)}A${RIM} ${RIM} 0 0 1 ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}Z`);
    }
    if (hasCycle && aurora.cycleState().on && !g && now - lastSync > 200) { lastSync = now; sync(); } // WS-47: live readout
    raf = pop.hidden || ac.signal.aborted ? 0 : requestAnimationFrame(wedge);
  }
  // placed next to the map controls: above the bar on desktop, left of the column on phones (style.css <= 760 px)
  function position() {
    const c = (btn.closest('.controls') || btn).getBoundingClientRect();
    if (innerWidth <= 760) {
      pop.style.left = 'auto'; pop.style.right = `${Math.round(innerWidth - c.left + 8)}px`;
      pop.style.width = `${Math.min(264, Math.round(c.left - 16))}px`;
      pop.style.bottom = `${Math.round(innerHeight - c.bottom)}px`;
    } else {
      const w = pop.offsetWidth || 264;
      pop.style.right = 'auto'; pop.style.width = '';
      pop.style.left = `${Math.round(Math.max(8, Math.min(innerWidth - w - 8, c.left)))}px`;
      pop.style.bottom = `${Math.round(innerHeight - c.top + 10)}px`;
    }
    pop.style.maxHeight = `${Math.round(innerHeight - parseFloat(pop.style.bottom) - 8)}px`;
    fit(pop); // VIS-128: then clear of the time bar, an open card and the Explore button (chrome/popup.js, as every popover)
  }
  function open(on, focus = false) {
    pop.hidden = !on; btn.setAttribute('aria-expanded', String(on)); btn.classList.toggle('aur-open', on);
    if (on) { sync(); position(); if (!raf) wedge(); if (focus) $('#aur-on').focus(); }
  }
  // replaces ui.js's plain toggle on this button (on/off is the switch inside the popover)
  btn.onclick = (e) => { e.stopPropagation(); open(pop.hidden, e.detail === 0); }; // detail 0 = keyboard: focus the switch
  document.addEventListener('pointerdown', (e) => { if (!pop.hidden && !pop.contains(e.target) && !btn.contains(e.target)) open(false); }, { signal: ac.signal });
  // VIS-128: in the capture phase, and the press stops here: the map's own Escape (close the card, stop the tour) must not act too
  addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || pop.hidden) return;
    e.stopImmediatePropagation();
    const inside = pop.contains(document.activeElement);
    open(false); if (inside) btn.focus();
  }, { signal: ac.signal, capture: true });
  addEventListener('resize', () => { if (!pop.hidden) position(); }, { signal: ac.signal });
  sync();
  // the control was hidden, or its button drawn again: the popover goes with it
  const destroy = () => { ac.abort(); cancelEase(); clearTimeout(urlT); pop.remove(); };
  return { sync, open, el: pop, destroy };
}
